<template>
  <div class="product-list-container">
    <!-- Header elegante -->
    <section class="hero is-dark">
      <div class="hero-body py-5">
        <div class="container">
          <div class="level is-mobile">
            <div class="level-left">
              <div class="level-item">
                <div>
                  <h1 class="title is-3 has-text-dark mb-2">
                    <i class="fa fa-store mr-3 has-text-primary"></i>
                    {{ $t("our products") }}
                  </h1>
                  <p class="subtitle is-6 has-text-grey">
                    {{
                      $t("discover amazing products") ||
                      "Descubre productos increíbles"
                    }}
                  </p>
                </div>
              </div>
            </div>
            <div class="level-right">
              <div class="level-item">
                <div class="field has-addons">
                  <div class="control has-icons-left">
                    <input
                      class="input is-rounded search-input"
                      type="text"
                      :placeholder="
                        $t('search products') || 'Buscar productos...'
                      "
                      v-model="search_query"
                    />
                    <span class="icon is-small is-left">
                      <i class="fa fa-search"></i>
                    </span>
                  </div>
                </div>
              </div>
              <div class="level-item">
                <button
                  class="button is-primary is-rounded cart-button"
                  @click="toggle_cart"
                  :class="{
                    'is-loading': products.some((p) => p.adding),
                  }"
                >
                  <span class="icon">
                    <i class="fa fa-shopping-cart"></i>
                  </span>
                  <span class="cart-text">
                    {{ $t("cart") }}
                    <span class="tag is-dark is-rounded ml-1">{{
                      cart_items.length
                    }}</span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <!-- Contenido principal -->
    <section class="section main-content">
      <div class="container">
        <!-- Filtros de categoría elegantes -->
        <div class="category-filters mb-6">
          <div class="has-text-centered mb-4">
            <h2 class="title is-5 has-text-dark mb-3">
              <i class="fa fa-filter mr-2"></i>
              {{ $t("filter by category") }}
            </h2>
          </div>
          <div class="category-tags">
            <span
              class="tag is-medium category-tag"
              :class="{ 'is-primary is-dark': active_category === 'all' }"
              @click="set_category('all')"
            >
              <i class="fa fa-th mr-2"></i>
              {{ $t("all") }}
              <span class="tag is-rounded is-small ml-2">{{
                products.length
              }}</span>
            </span>
            <span
              class="tag is-medium category-tag"
              v-for="category in categories"
              :key="category"
              :class="{ 'is-primary is-dark': active_category === category }"
              @click="set_category(category)"
            >
              <i class="fa fa-tag mr-2"></i>
              {{ category }}
              <span class="tag is-rounded is-small ml-2">
                {{
                  products.filter((p) => p.categories.includes(category)).length
                }}
              </span>
            </span>
          </div>
        </div>

        <!-- Lista de productos -->
        <div class="columns is-multiline is-centered">
          <div
            class="column is-6-mobile is-4-tablet is-3-desktop"
            v-for="product in filtered_products"
            :key="product.product_id"
          >
            <div class="card product-card">
              <div class="card-image">
                <figure class="image is-4by3">
                  <v-lazy-image
                    v-if="product.image"
                    :src="`${api_host}/drive/static/${product.image}`"
                    :alt="product.name"
                    class="product-image"
                  />
                  <div v-else class="image-placeholder">
                    <i class="fa fa-image"></i>
                  </div>
                </figure>
                <div class="price-badge">
                  {{ format_currency(product.price.toFixed(2)) }}
                </div>
              </div>

              <div class="card-content">
                <div class="content">
                  <h3 class="title is-5 mb-2 has-text-black">
                    {{ product.name }}
                  </h3>
                  <p class="subtitle is-7 has-text-grey mb-3">
                    {{ product.description }}
                  </p>

                  <div class="tags mb-4">
                    <span
                      v-for="cat in product.categories.slice(0, 2)"
                      :key="cat"
                      class="tag is-small is-rounded"
                    >
                      {{ cat }}
                    </span>
                  </div>

                  <!-- <div class="has-text-centered mb-4">
                    <span
                      class="has-text-weight-bold has-text-primary is-size-4"
                    >
                      {{ format_currency(product.price.toFixed(2)) }}
                    </span>
                  </div> -->

                  <button
                    class="button is-primary is-fullwidth"
                    :class="{
                      'is-loading': product.adding,
                    }"
                    @click="add_to_cart(product, $event)"
                  >
                    <span class="icon">
                      <i class="fa fa-cart-plus"></i>
                    </span>
                    <span>{{ $t("add to cart") || "Agregar al carrito" }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Estado vacío mejorado -->
        <div v-if="filtered_products.length === 0" class="empty-state">
          <div class="empty-state-content">
            <div class="empty-icon">
              <i class="fa fa-search"></i>
            </div>
            <h3 class="title is-4 has-text-grey">
              {{ $t("no products found") }}
            </h3>
            <p class="subtitle is-6 has-text-grey">
              {{
                $t("try adjusting your search") ||
                "Intenta ajustar tu búsqueda o filtros"
              }}
            </p>
            <button class="button is-primary is-medium" @click="reset_filters">
              <span class="icon">
                <i class="fa fa-refresh"></i>
              </span>
              <span>{{ $t("show all products") }}</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
// import "animate.css";
import { get, api_host, format_currency } from "@/utils";
import VLazyImage from "v-lazy-image";

export default {
  name: "ProductList",
  components: {
    VLazyImage,
  },
  data() {
    return {
      api_host,
      search_query: "",
      active_category: "all",
      cart_items: [],
      products: [],
    };
  },
  methods: {
    format_currency,
    toggle_cart() {
      let v = this;
      v.$emit("toggle_cart", v.cart_items);
    },
    set_cart_items(cart_items) {
      this.cart_items = cart_items;
    },
    set_category(category) {
      this.active_category = category;
    },
    reset_filters() {
      this.active_category = "all";
      this.search_query = "";
    },
    add_to_cart(product, $event) {
      let v = this;
      const existing_item = this.cart_items.find(
        (item) => item.product_id === product.product_id
      );

      let trgt_product = v.products.find(
        (p) => p.product_id === product.product_id
      );
      if (trgt_product) {
        trgt_product.adding = true;
        setTimeout(() => {
          trgt_product.adding = false;
        }, 500);
      }

      if (existing_item) existing_item.quantity += 1;
      else {
        this.cart_items.push({
          ...product,
          quantity: 1,
        });
      }
    },
    retrieve_products() {
      let v = this;
      v.$root.loader(true);
      get("/online-store/product/all")
        .then((response) => {
          v.products = response;
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          v.$root.loader(false);
        });
    },
  },
  computed: {
    categories() {
      let all_categories = this.products.reduce((acc, product) => {
        acc = acc.concat(product.categories);
        return acc;
      }, []);
      // Filtrar categorías duplicadas
      all_categories = all_categories.filter(
        (category, index) => all_categories.indexOf(category) === index
      );
      // Ordenar categorías alfabéticamente
      all_categories.sort((a, b) => a.localeCompare(b));
      return all_categories;
      // const unique_categories = new Set(this.products.map((p) => p.category));
      // return Array.from(unique_categories);
    },
    filtered_products() {
      let result = this.products;

      // Filtrar por categoría
      if (this.active_category !== "all") {
        result = result.filter((p) =>
          p.categories.includes(this.active_category)
        );
      }

      // Filtrar por búsqueda
      if (this.search_query) {
        const query = this.search_query.toLowerCase();
        result = result.filter(
          (p) =>
            p.name.toLowerCase().includes(query) ||
            p.description.toLowerCase().includes(query)
        );
      }

      return result;
    },
    is_mobile() {
      return window.innerWidth <= 768;
    },
  },
  mounted() {
    this.retrieve_products();
  },
};
</script>

<style scoped>
/* Variables CSS para consistencia */
:root {
  --primary-gradient: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  --secondary-gradient: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  --success-gradient: linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%);
  --shadow-light: 0 2px 10px rgba(0, 0, 0, 0.1);
  --shadow-medium: 0 8px 25px rgba(0, 0, 0, 0.15);
  --shadow-heavy: 0 15px 35px rgba(0, 0, 0, 0.2);
  --border-radius: 12px;
  --transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.product-list-container {
  min-height: 100vh;
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
}

/* Header hero mejorado */
.hero.is-dark {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.hero.is-dark .title {
  color: white !important;
}

.hero.is-dark .subtitle {
  color: rgba(255, 255, 255, 0.8) !important;
}

.search-input {
  min-width: 300px;
  border: none;
  box-shadow: var(--shadow-light);
  transition: var(--transition);
}

.search-input:focus {
  box-shadow: var(--shadow-medium);
  transform: translateY(-1px);
}

.cart-button {
  background: var(--success-gradient) !important;
  border: none;
  box-shadow: var(--shadow-light);
  transition: var(--transition);
  font-weight: 600;
}

.cart-button:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-medium);
}

.cart-text {
  display: flex;
  align-items: center;
}

/* Filtros de categoría elegantes */
.category-filters {
  padding: 2rem 0;
}

.category-tags {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
}

.category-tag {
  cursor: pointer;
  transition: var(--transition);
  border: 2px solid transparent;
  font-weight: 500;
  padding: 0.8rem 1.2rem;
  border-radius: var(--border-radius);
  background: white;
  box-shadow: var(--shadow-light);
}

.category-tag:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-medium);
  border-color: #667eea;
}

.category-tag.is-primary.is-dark {
  background: var(--primary-gradient) !important;
  color: rgb(39, 39, 39) !important;
  border-color: transparent;
}

/* Grid de productos moderno */
.product-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  border: none;
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: var(--shadow-light);
  transition: var(--transition);
  background: white;
  position: relative;
}

.product-card:hover {
  transform: translateY(-8px);
  box-shadow: var(--shadow-heavy);
}

.product-card .card-image {
  position: relative;
  overflow: hidden;
}

.product-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: var(--transition);
}

.product-card:hover .product-image {
  transform: scale(1.05);
}

.image-placeholder {
  width: 100%;
  height: 200px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a0aec0;
  font-size: 3rem;
}

.price-badge {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 0.3rem 0.6rem;
  border-radius: 15px;
  font-weight: 600;
  font-size: 0.9rem;
  box-shadow: var(--shadow-light);
  z-index: 2;
  opacity: 0.8;
}

.card-content {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.card-content .content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* Estado vacío elegante */
.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 4rem 2rem;
}

.empty-state-content {
  text-align: center;
  max-width: 400px;
}

.empty-icon {
  font-size: 4rem;
  color: #cbd5e0;
  margin-bottom: 2rem;
}

.empty-state .title {
  margin-bottom: 1rem;
}

.empty-state .subtitle {
  margin-bottom: 2rem;
}

/* Animaciones de entrada */
.product-card {
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsividad mejorada */
@media screen and (max-width: 768px) {
  .search-input {
    min-width: 200px;
  }

  .cart-text span {
    display: none;
  }

  .category-tags {
    justify-content: flex-start;
    overflow-x: auto;
    padding-bottom: 1rem;
    flex-wrap: nowrap;
  }

  .category-tag {
    flex-shrink: 0;
  }

  .level {
    flex-direction: column;
    gap: 1rem;
  }

  .level-left,
  .level-right {
    width: 100%;
  }

  .level-right {
    justify-content: space-between;
  }

  .product-card {
    margin-bottom: 1rem;
  }
}

@media screen and (max-width: 480px) {
  .hero-body {
    padding: 2rem 1rem !important;
  }

  .search-input {
    min-width: 150px;
  }
} /* Mejoras de accesibilidad */
.category-tag:focus,
.add-to-cart-btn:focus,
.cart-button:focus {
  outline: 3px solid rgba(102, 126, 234, 0.5);
  outline-offset: 2px;
}

/* Loading states mejorados */
.is-loading {
  position: relative;
  color: transparent !important;
}

.is-loading::after {
  animation: spinAround 500ms infinite linear;
  border: 2px solid #dbdbdb;
  border-radius: 290486px;
  border-right-color: transparent;
  border-top-color: transparent;
  content: "";
  display: block;
  height: 1em;
  position: absolute;
  left: calc(50% - (1em / 2));
  top: calc(50% - (1em / 2));
  width: 1em;
}

@keyframes spinAround {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(359deg);
  }
}
</style>
